/* eslint-disable @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';
import pick from 'lodash/pick';
import { saveAs } from 'file-saver';

type ExportDataParams = {
  data: any[];
  columns?: string[];
  columnLabels?: string[];
  columnOptions?: any[];
  sheetName?: string;
  fileName?: string;
};

const useExcelExport = () => {
  const exportData = useCallback(async (params: ExportDataParams) => {
    const { data, columns, columnLabels, columnOptions, sheetName, fileName } = params;

    const ExcelJS = (await import('exceljs')).default;
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet(sheetName || 'Sheet1');

    let dataToExport = data;
    if (columns) {
      dataToExport = data.map((dataObject) => pick(dataObject, columns));
    }

    if (columnLabels) {
      worksheet.addRow(columnLabels);
      dataToExport.forEach((row) => {
        worksheet.addRow(Object.values(row));
      });
    } else if (dataToExport.length > 0) {
      const keys = Object.keys(dataToExport[0]);
      worksheet.columns = keys.map((key) => ({ header: key, key }));
      dataToExport.forEach((row) => worksheet.addRow(row));
    }

    if (columnOptions) {
      columnOptions.forEach((opt, i) => {
        if (opt && opt.wpx && worksheet.columns[i]) {
          // xlsx wpx (pixels) → exceljs width (characters, ~7px each)
          worksheet.columns[i].width = Math.round(opt.wpx / 7);
        }
      });
    }

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${fileName}.xlsx`);
  }, []);

  return exportData;
};

export default useExcelExport;
